import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Paperclip, Upload, Download, Trash2, Loader2, AlertCircle, RefreshCw, File as FileIcon } from "lucide-react";
import { Button } from "../ui/button";

/**
 * Section 21 — Cost Attachments UI
 * Reusable panel for attaching files to a cost or outfit-procurement record.
 *
 * Props:
 *   parentType: "business_cost" | "outfit_procurement"
 *   parentId:   string (required — will not render until present)
 *   compact?:   boolean (tightens spacing for dialog usage)
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 100 * 1024 * 1024;

const ALLOWED_EXTS = [
  "pdf", "jpg", "jpeg", "png", "gif", "webp", "heic", "heif",
  "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv", "txt",
];
const ACCEPT = ALLOWED_EXTS.map((e) => `.${e}`).join(",");

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` };
}

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function extOf(name) {
  if (!name || !name.includes(".")) return "";
  return name.split(".").pop().toLowerCase();
}

export function AttachmentsPanel({ parentType, parentId, compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [lastFailedFile, setLastFailedFile] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const inputRef = useRef(null);
  const cancelRef = useRef(null);

  const load = useCallback(async () => {
    if (!parentId) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/attachments?parent_type=${parentType}&parent_id=${encodeURIComponent(parentId)}`,
        { headers: auth() },
      );
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load attachments");
    } finally {
      setLoading(false);
    }
  }, [parentType, parentId]);

  useEffect(() => { load(); }, [load]);

  const doUpload = async (file) => {
    // Client-side pre-checks
    const ext = extOf(file.name);
    if (!ext || !ALLOWED_EXTS.includes(ext)) {
      toast.error(`File type “.${ext || "unknown"}” is not allowed`);
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`File is ${fmtSize(file.size)} — 100 MB is the maximum`);
      return;
    }
    setUploading(true);
    setProgress(0);
    setError(null);
    setLastFailedFile(null);

    const form = new FormData();
    form.append("parent_type", parentType);
    form.append("parent_id", parentId);
    form.append("file", file);

    const controller = new AbortController();
    cancelRef.current = controller;

    try {
      const r = await axios.post(`${API}/admin/attachments`, form, {
        headers: { ...auth() },  // let the browser set the multipart boundary
        onUploadProgress: (evt) => {
          if (evt.total) {
            setProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        },
        signal: controller.signal,
      });
      const att = r.data?.attachment;
      if (att) {
        setItems((prev) => [att, ...prev]);
        toast.success(`Uploaded ${att.filename}`);
      }
      setProgress(100);
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg;
      if (detail === "storage_not_configured") {
        msg = "File storage is not configured on this server (EMERGENT_LLM_KEY missing). Please contact an administrator.";
      } else if (detail === "file_too_large") {
        msg = "File is over the 100 MB limit.";
      } else {
        msg = detail ? String(detail).replace(/_/g, " ") : (e.message || "Upload failed");
      }
      setError(msg);
      setLastFailedFile(file);
      toast.error(msg);
    } finally {
      setUploading(false);
      cancelRef.current = null;
      // Clear the file input so the same file can be re-selected
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) doUpload(f);
  };

  const onRetry = () => {
    if (lastFailedFile) doUpload(lastFailedFile);
  };

  const onDownload = async (att) => {
    try {
      const r = await axios.get(`${API}/admin/attachments/${att.id}/download`, {
        headers: auth(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const onDelete = async (att) => {
    if (!window.confirm(`Delete ${att.filename}? This cannot be undone from the UI.`)) return;
    setDeletingId(att.id);
    try {
      await axios.delete(`${API}/admin/attachments/${att.id}`, { headers: auth() });
      setItems((prev) => prev.filter((x) => x.id !== att.id));
      toast.success("Attachment deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  if (!parentId) {
    return (
      <div className="text-[11px] text-[#676664]/50 italic" data-testid="attachments-panel-disabled">
        Save the record first to add attachments.
      </div>
    );
  }

  return (
    <div
      className={`border border-[#282828]/10 bg-[#FAF8F0]/40 ${compact ? "p-3" : "p-4"}`}
      data-testid="attachments-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[#676664] text-xs uppercase tracking-[0.08em]">
          <Paperclip className="w-3.5 h-3.5 text-[#9A7B4F]" strokeWidth={1.5} />
          Attachments
          <span className="text-[#676664]/50 normal-case tracking-normal" data-testid="attachments-count">
            ({items.length})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={onPick}
            data-testid="attachments-file-input"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-[#9A7B4F]/40 text-[#9A7B4F] hover:bg-[#9A7B4F]/5 h-8 px-3"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            data-testid="attachments-upload-btn"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />}
            {uploading ? `${progress}%` : "Upload"}
          </Button>
        </div>
      </div>

      {uploading && (
        <div className="mb-3" data-testid="attachments-progress">
          <div className="h-1.5 bg-[#282828]/10 overflow-hidden">
            <div
              className="h-full bg-[#9A7B4F] transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="text-[10px] text-[#676664]/60 mt-1">Uploading… {progress}%</div>
        </div>
      )}

      {error && !uploading && (
        <div className="mb-3 flex items-start gap-2 bg-[#B23A48]/5 border border-[#B23A48]/30 px-3 py-2" data-testid="attachments-error">
          <AlertCircle className="w-3.5 h-3.5 text-[#B23A48] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
          <div className="flex-1">
            <div className="text-[11px] text-[#B23A48]">{error}</div>
            {lastFailedFile && (
              <button
                type="button"
                onClick={onRetry}
                className="text-[11px] text-[#9A7B4F] hover:text-[#8B6B3F] mt-1 inline-flex items-center gap-1"
                data-testid="attachments-retry-btn"
              >
                <RefreshCw className="w-3 h-3" strokeWidth={1.5} />
                Retry upload
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-4" data-testid="attachments-loading">
          <Loader2 className="w-4 h-4 animate-spin text-[#9A7B4F]" strokeWidth={1.5} />
        </div>
      ) : items.length === 0 ? (
        <div className="text-[11px] text-[#676664]/50 italic py-2" data-testid="attachments-empty">
          No files attached. Max 100 MB per file. Accepted: PDF, images, Word/Excel/PowerPoint, CSV, TXT.
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="attachments-list">
          {items.map((att) => (
            <li
              key={att.id}
              className="flex items-center gap-2 bg-white border border-[#282828]/10 px-2.5 py-1.5"
              data-testid={`attachment-row-${att.id}`}
            >
              <FileIcon className="w-3.5 h-3.5 text-[#676664]/50 flex-shrink-0" strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] text-[#282828] truncate">{att.filename}</div>
                <div className="text-[10px] text-[#676664]/50">
                  {fmtSize(att.size)} · {new Date(att.created_at).toLocaleDateString("en-AU")}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onDownload(att)}
                className="text-[#676664]/60 hover:text-[#9A7B4F] p-1"
                data-testid={`attachment-download-${att.id}`}
                title="Download"
              >
                <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={() => onDelete(att)}
                disabled={deletingId === att.id}
                className="text-[#676664]/60 hover:text-[#B23A48] p-1 disabled:opacity-50"
                data-testid={`attachment-delete-${att.id}`}
                title="Delete"
              >
                {deletingId === att.id
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AttachmentsPanel;
